import type { ILevelsOptions, INumberingOptions } from 'docx';
import { AlignmentType, LevelFormat } from 'docx';

import type { DocumentContext } from './internal';
import { createRunProperties } from './internal';
import type { ListLevel, ListLevelDefinition } from '../../../slate-api';
import {
  createLevelCustomText,
  isListLevel,
  ListLevelAlignment,
  ListMarkerStyleName,
} from '../../../slate-api';

type ILevelsOptionsSubset = Required<Pick<ILevelsOptions, 'format' | 'text'>>;

function markerStyleToLevelOptions(
  levelDefinition: ListLevelDefinition,
  options: { listLevel: ListLevel }
): ILevelsOptionsSubset {
  const { listLevel } = options;
  const levelText =
    levelDefinition.style.customText ??
    createLevelCustomText(levelDefinition.style.name, {
      listLevel,
      prefix: levelDefinition.style.prefix,
      suffix: levelDefinition.style.suffix,
    });

  switch (levelDefinition.style.name) {
    case ListMarkerStyleName.DECIMAL:
      return { format: LevelFormat.DECIMAL, text: levelText };
    case ListMarkerStyleName.DECIMAL_LEADING_ZERO:
      return { format: LevelFormat.DECIMAL_ZERO, text: levelText };
    case ListMarkerStyleName.DECIMAL_SECTION:
      return {
        format: LevelFormat.DECIMAL,
        text: levelText,
      };
    case ListMarkerStyleName.LOWER_LATIN:
      return { format: LevelFormat.LOWER_LETTER, text: levelText };
    case ListMarkerStyleName.LOWER_ROMAN:
      return { format: LevelFormat.LOWER_ROMAN, text: levelText };
    case ListMarkerStyleName.UPPER_LATIN:
      return { format: LevelFormat.UPPER_LETTER, text: levelText };
    case ListMarkerStyleName.UPPER_ROMAN:
      return { format: LevelFormat.UPPER_ROMAN, text: levelText };
    case ListMarkerStyleName.BULLET:
    case ListMarkerStyleName.BLACK_CIRCLE:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.BLACK_DIAMOND_BLACK_CIRCLE:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.BLACK_DIAMOND_MINUS_WHITE_X:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.BLACK_SQUARE:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.BLACK_STAR:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.LOWER_RIGHT_SHADOWED_WHITE_SQUARE:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.RIGHTWARDS_ARROW:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.THREE_D_TOP_LIGHTED_RIGHTWARDS_ARROWHEAD:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.WHITE_CIRCLE:
      return { format: LevelFormat.BULLET, text: levelText };
    case ListMarkerStyleName.CUSTOM:
      return { format: LevelFormat.DECIMAL, text: levelText };
  }
}

export function createNumberingOptionsConfigs(
  context: DocumentContext
): INumberingOptions['config'] {
  const { listDefinitions } = context;

  return Object.entries(listDefinitions).map(
    ([listDefinitionId, listDefinition]): INumberingOptions['config'][number] => ({
      levels: Object.keys(listDefinition).map(levelString => {
        const listLevel = parseInt(levelString, 10);
        if (!isListLevel(listLevel)) throw new Error('Expected list level number.');

        const levelDefinition = listDefinition[listLevel];
        const { format, text } = markerStyleToLevelOptions(levelDefinition, { listLevel });

        const startProps =
          levelDefinition.start !== undefined ? { start: levelDefinition.start } : {};

        return {
          alignment: listLevelAlignmentToDOCXAlignment(
            levelDefinition.alignment ?? ListLevelAlignment.Start
          ),
          format,
          level: listLevel,
          style: {
            run: createRunProperties(levelDefinition.markerFormats ?? {}),
          },
          text,
          ...startProps,
        };
      }),
      reference: listDefinitionId,
    })
  );
}

export function listLevelAlignmentToDOCXAlignment(
  alignment: ListLevelAlignment
): ILevelsOptions['alignment'] {
  switch (alignment) {
    case ListLevelAlignment.Start:
      return AlignmentType.START;
    case ListLevelAlignment.End:
      return AlignmentType.END;
  }
}
